import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Folder, FolderOpen, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useWorkbookQuery } from '@/hooks/content-generator/use-workbook-query';
import { useLocalStorageState } from '@/hooks/ai-agents/useLocalStorageState';
import { getProjectIndex } from '@/utils/project-navigation-cache';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/layout/use-mobile';

interface ProjectsDropdownProps {
  isActive?: boolean;
  className?: string;
}

const ProjectsDropdown: React.FC<ProjectsDropdownProps> = ({ 
  isActive = false, 
  className 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isModalOpen, closeModal } = useLocalStorageState();
  const { isMobile } = useIsMobile();
  
  // Fetch projects using the same hook as project-manager page
  const { data: projectsData, isLoading } = useWorkbookQuery({ 
    filterByStatus: 'all' 
  });
  
  const projects = projectsData?.projects || [];
  
  // Sort projects: favorites first, then by updated_at (most recent first)
  const sortedProjects = [...projects].sort((a, b) => {
    // Favorites first
    if (a.isFavorite && !b.isFavorite) return -1;
    if (!a.isFavorite && b.isFavorite) return 1;
    // Then by updated date (most recent first)
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
  
  // Show first 5 projects
  const displayProjects = sortedProjects.slice(0, 5);
  const totalProjectsCount = projects.length;
  
  // 🎯 PERFORMANCE OPTIMIZED: URL-based detection + cache lookup
  const currentProjectInfo = useMemo(() => {
    // Check if we're in a project route: /project/{id}/*
    const isProjectRoute = location.pathname.startsWith('/project/');
    if (!isProjectRoute) {
      return { isInProject: false, projectName: null };
    }
    
    // Extract project ID from URL: /project/{id}/tasks -> {id}
    const pathSegments = location.pathname.split('/');
    const projectId = pathSegments[2]; // /project/{id}/...
    
    if (!projectId) {
      return { isInProject: false, projectName: null };
    }
    
    // 🚀 INSTANT LOOKUP: Get project name from cache (no network call)
    try {
      const projectIndex = getProjectIndex();
      if (!projectIndex?.projects) {
        return { isInProject: true, projectName: null };
      }
      
      // Find project in cache by ID
      const project = projectIndex.projects.find(p => p.id === projectId);
      const projectName = project?.title || null;
      
      return { 
        isInProject: true, 
        projectName,
        projectId 
      };
    } catch (error) {
      console.warn('[ProjectsDropdown] Cache lookup error:', error);
      return { isInProject: true, projectName: null };
    }
  }, [location.pathname]);
  
  // Handler to close chat modal when navigation links are clicked
  const handleLinkClick = () => {
    if (isModalOpen) {
      closeModal();
    }
  };
  
  const handleProjectClick = (projectId: string) => {
    handleLinkClick();
    navigate(`/project/${projectId}/tasks`);
  };
  
  const handleViewAllClick = () => {
    handleLinkClick();
    navigate('/project-manager');
  };

  // 🎨 DYNAMIC BUTTON TEXT: Show project name when in project, "Project Manager" when on project-manager page, otherwise "Project Manager" (desktop) or "Projects" (mobile)
  const buttonText = useMemo(() => {
    // On mobile, always show "Projects"
    if (isMobile) {
      return "Projects";
    }
    
    // Desktop behavior
    if (currentProjectInfo.isInProject && currentProjectInfo.projectName) {
      return currentProjectInfo.projectName;
    }
    if (location.pathname === '/project-manager') {
      return "Project Manager";
    }
    return "Project Manager";
  }, [currentProjectInfo.isInProject, currentProjectInfo.projectName, location.pathname, isMobile]);

  // 📱 MOBILE TEXT TRUNCATION: Limit text to 10 characters on mobile
  const displayText = useMemo(() => {
    if (isMobile && buttonText.length > 10) {
      return buttonText.substring(0, 10) + '...';
    }
    return buttonText;
  }, [buttonText, isMobile]);

  const ButtonIcon = currentProjectInfo.isInProject ? FolderOpen : Folder;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <motion.div
          className={cn(
            "relative rounded-lg",
            // Match NavbarIconButton dimensions and styling
            "px-3 py-2 sm:px-3 sm:py-2 h-12",
            isActive
              ? "bg-gradient-to-br from-brandBlue/15 to-brandPurple/10 dark:from-brandBlue/25 dark:to-brightPurple/20"
              : "bg-gradient-to-br from-white/30 to-white/10 dark:from-slate-700/20 dark:to-slate-800/10",
            isActive
              ? "border border-brandBlue/30 dark:border-brightPurple/30"
              : "border border-white/5 dark:border-slate-700/20",
            "backdrop-blur-md",
            "text-slate-700 dark:text-slate-200",
            "shadow-sm hover:shadow-md transition-all duration-300",
            "cursor-pointer",
            "flex items-center gap-2",
            className
          )}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <ButtonIcon size={18} />
          <span className="font-medium truncate">{displayText}</span>
          <ChevronDown size={16} className="ml-1 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        </motion.div>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent 
        align="start" 
        className="w-64 p-0 border border-accent/20 shadow-lg backdrop-blur-xl 
          bg-white/95 dark:bg-black/90 rounded-xl overflow-hidden"
        sideOffset={4}
      >
        <div className="p-2">
          {isLoading ? (
            <DropdownMenuItem disabled className="px-3 py-2.5">
              Loading projects...
            </DropdownMenuItem>
          ) : displayProjects.length === 0 ? (
            <DropdownMenuItem disabled className="px-3 py-2.5">
              No projects found
            </DropdownMenuItem>
          ) : (
            displayProjects.map((project) => (
              <DropdownMenuItem 
                key={project.id} 
                asChild 
                className="hover:bg-transparent focus:bg-transparent"
              >
                <motion.button
                  onClick={() => handleProjectClick(project.id)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium w-full transition-all duration-200",
                    "text-slate-700 dark:text-slate-200 hover:text-white",
                    "hover:bg-gradient-to-r hover:from-brandPurple hover:to-brandBlue",
                    "group text-left",
                    // Highlight current project if we're viewing it
                    currentProjectInfo.projectId === project.id && 
                    "bg-gradient-to-r from-brandPurple/10 to-brandBlue/10 dark:from-brandPurple/20 dark:to-brandBlue/20"
                  )}
                  whileTap={{ scale: 0.97 }}
                >
                  <FolderOpen size={16} className={cn(
                    "flex-shrink-0",
                    currentProjectInfo.projectId === project.id 
                      ? "text-brandPurple dark:text-brandBlue" 
                      : "text-slate-500 dark:text-slate-400 group-hover:text-white"
                  )} />
                  <span className="flex-1 truncate">{project.title}</span>
                  {project.isFavorite && (
                    <div className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
                  )}
                </motion.button>
              </DropdownMenuItem>
            ))
          )}
        </div>
        
        <DropdownMenuSeparator className="mx-2 my-1 bg-slate-200/50 dark:bg-slate-700/50" />
        <div className="p-2">
          <DropdownMenuItem asChild className="hover:bg-transparent focus:bg-transparent">
            <motion.button
              onClick={handleViewAllClick}
              className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium w-full transition-all duration-200
                text-slate-700 dark:text-slate-200 hover:text-white
                hover:bg-gradient-to-r hover:from-brandPurple hover:to-brandBlue
                group"
              whileTap={{ scale: 0.97 }}
            >
              <span>Project Manager</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 group-hover:text-white">
                ({totalProjectsCount})
              </span>
            </motion.button>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ProjectsDropdown; 